'use server';

import { cookies, headers } from 'next/headers';
import { redirect } from 'next/navigation';
import { env } from '@/config/env';
import { ApiError, apiRequest } from '@/lib/api-client';
import { getToken, type TAuthUser } from '@/lib/auth';
import { rememberLocale } from '@/lib/i18n/locale-cookie';
import { RETURN_TO_COOKIE, SESSION_COOKIE } from '@/lib/session-cookie';
import { TRANSLATIONS, type TTranslationKey } from '@/i18n/config';

/** `error` is a translation key; the form shows it in the reader's language. */
export type TLoginState = { error: TTranslationKey | null; email: string };

type TLoginResponse = { token: string; exp: number; user: TAuthUser };

/**
 * Only a path on this dashboard. Resolved the way a browser would, so `//evil.com`,
 * `/\evil.com` (browsers read the backslash as a slash) and `/.//evil.com` cannot send
 * someone elsewhere.
 */
const safeNext = (next: string | null): string => {
  if (typeof next !== 'string' || !next.startsWith('/')) return '/';
  const base = 'http://dashboard.invalid';
  try {
    const url = new URL(next, base);
    // `/.//evil.com` normalizes to `//evil.com`: same origin here, another site once redirected.
    return url.origin === base && !url.pathname.startsWith('//') ? `${url.pathname}${url.search}${url.hash}` : '/';
  } catch {
    return '/';
  }
};

export const login = async (_previous: TLoginState, formData: FormData): Promise<TLoginState> => {
  const email = String(formData.get('email') ?? '').trim();
  const password = String(formData.get('password') ?? '');

  if (!email || !password) return { error: TRANSLATIONS.auth.enter_email_password, email };

  let response: TLoginResponse;
  try {
    response = await apiRequest<TLoginResponse>('/api/users/login', { method: 'POST', body: { email, password } });
  } catch (error) {
    // Payload answers 401 for a wrong address and a wrong password alike, so the
    // message does not say which one it was.
    if (error instanceof ApiError && error.status === 401) {
      return { error: TRANSLATIONS.auth.sign_in_failed, email };
    }
    return { error: TRANSLATIONS.auth.server_unreachable, email };
  }

  const jar = await cookies();
  jar.set(SESSION_COOKIE, response.token, {
    httpOnly: true,
    secure: env.isProduction,
    sameSite: 'lax',
    path: '/',
    // The cookie dies with the token (`exp` is in seconds), so a stale one is not
    // left behind for the proxy to mistake for a session.
    expires: new Date(response.exp * 1000),
  });
  // The dashboard opens in the account's own language.
  rememberLocale(jar, response.user?.language);

  // Back to the page the proxy sent them away from, once; the cookie is not reused.
  const returnTo = jar.get(RETURN_TO_COOKIE)?.value ?? null;
  jar.delete(RETURN_TO_COOKIE);
  redirect(safeNext(returnTo));
};

export const logout = async () => {
  const token = await getToken();
  if (token) {
    // Best effort: the cookie is what signs the browser out, whatever the CMS says.
    await apiRequest('/api/users/logout', { method: 'POST', token }).catch(() => null);
  }

  (await cookies()).delete(SESSION_COOKIE);
  redirect('/login');
};

/** `error` is a translation key; the form shows it in the reader's language. */
export type TSetPasswordState = { error: TTranslationKey | null };

/** The shortest password the dashboard accepts. */
const PASSWORD_MIN_LENGTH = 8;

/** When a Payload JWT stops working (`exp`, in seconds), read from the token itself. */
const expiryOf = (token: string): Date | undefined => {
  try {
    const payload = JSON.parse(Buffer.from(token.split('.')[1] ?? '', 'base64url').toString('utf8'));
    return typeof payload.exp === 'number' ? new Date(payload.exp * 1000) : undefined;
  } catch {
    return undefined;
  }
};

/**
 * The page an invitation or a password-reset e-mail links to: the person chooses their
 * password with the token from the link, and is signed in with it straight away.
 */
export const setPassword = async (_previous: TSetPasswordState, formData: FormData): Promise<TSetPasswordState> => {
  const token = String(formData.get('token') ?? '');
  const password = String(formData.get('password') ?? '');
  const confirm = String(formData.get('confirm') ?? '');

  if (!token) return { error: TRANSLATIONS.set_password.error_link };
  if (password.length < PASSWORD_MIN_LENGTH) return { error: TRANSLATIONS.set_password.error_short };
  if (password !== confirm) return { error: TRANSLATIONS.set_password.error_mismatch };

  let response: { token?: string; user?: TAuthUser };
  try {
    response = await apiRequest<{ token?: string; user?: TAuthUser }>('/api/users/reset-password', {
      method: 'POST',
      body: { token, password },
    });
  } catch (error) {
    // Payload answers 4xx for a token that is wrong, used or expired — one message for all.
    if (error instanceof ApiError && error.status < 500) return { error: TRANSLATIONS.set_password.error_link };
    return { error: TRANSLATIONS.auth.server_unreachable };
  }

  if (response.token) {
    const jar = await cookies();
    jar.set(SESSION_COOKIE, response.token, {
      httpOnly: true,
      secure: env.isProduction,
      sameSite: 'lax',
      path: '/',
      expires: expiryOf(response.token),
    });
    rememberLocale(jar, response.user?.language);
    redirect('/');
  }
  // Password set but no session handed back: sign in as usual.
  redirect('/login');
};

export type TForgotPasswordState = { status: 'idle' | 'sent' | 'error'; email: string };

/**
 * Sends the account a link to choose a new password (the CMS's own reset e-mail, which leads
 * to /set-password). Says "sent" whether or not the address has an account, so the form
 * cannot be used to find out who has one.
 */
export const requestPasswordReset = async (
  _previous: TForgotPasswordState,
  formData: FormData,
): Promise<TForgotPasswordState> => {
  const email = String(formData.get('email') ?? '').trim().toLowerCase();
  if (!email || !email.includes('@')) return { status: 'error', email: '' };

  // The CMS limits these per visitor; it is called from this server, so it is told who asked.
  const forwarded = (await headers()).get('x-forwarded-for')?.split(',')[0]?.trim();
  const clientIp = forwarded || (await headers()).get('x-real-ip');
  try {
    await apiRequest('/api/users/forgot-password', { method: 'POST', body: { email }, clientIp });
  } catch (error) {
    // An unknown address is not an error worth telling; an unreachable CMS is. Too many
    // requests (429) is answered as "sent" too: the earlier e-mails are on their way.
    if (!(error instanceof ApiError && error.status < 500)) return { status: 'error', email };
  }
  return { status: 'sent', email };
};
